// ---------------------------------------------------------------------------
// playerView — what one player can legally know, and nothing more
// ---------------------------------------------------------------------------
//
// A bot decides from this, never from the war state. It mirrors what the war
// board shows a player (components/v2/hex/marks.js):
//
//   own side     everything: board, Grid, yield, structures lost
//   public       the round; the opponent's corridor board (their drones and
//                brood ledger, as the board shows it between rounds); both
//                kill counts
//   their Grid   per cell, only what the board draws on the opponent's half:
//                  - every crater, and whether a structure stood there (the
//                    dead ring)
//                  - the whole footprint of any structure that has taken a
//                    hit (every cell glows), as one known group
//                  - every rocket (drawn on intact, unscanned cells too)
//                  - scanned cells: a structure there (bullseye) or open land
//                  - every brood: footprint, construction state, egg
//                  - every intel stamp: distance, letter, dead
//                Never a structure id, never an unseen cell's contents, never
//                their yield.
//   memory       every cell that has ever held one of their broods
//                (opp.grid.pastBroodCells): the broods it can see now plus
//                the ones it remembers seeing. Structures are fixed when the
//                war starts and a brood can't be built on one, so those cells
//                never hold a structure — even after the brood is gone and
//                the board shows plain land there. The caller carries the
//                memory from round to round (playerView's `memory`).
//
// Cells are 'unknown' | 'empty' | 'structure' ('structure' = something stands
// or stood there: a structure or a Brood).

import { isStructureDestroyed, hasLegacyBroods, migrateBroods } from '../subplotGrid.js'
import { isBroodDead } from '../subplotBases.js'

const parse = (raw) => {
    if (raw == null || raw === '') return null
    if (typeof raw === 'string') return migrateBroods(JSON.parse(raw))
    return hasLegacyBroods(raw) ? migrateBroods(JSON.parse(JSON.stringify(raw))) : raw
}
const clone = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)))

// The opponent's Grid as a player sees it.
export function viewOfOpponentGrid(hfRaw) {
    const hf = parse(hfRaw)
    const n = hf?.subplots?.length ?? 0
    const cells = new Array(n)
    const groups = []
    const broods = []
    const rockets = []
    const stamps = []
    const grouped = new Set()
    for (let s = 0; s < n; s++) {
        const c = hf.subplots[s]
        let known = 'unknown'
        const st = c.structureId != null ? hf.structures[c.structureId] : null
        const brood = c.broodId != null ? hf.broods?.[c.broodId] : null
        if (c.destroyed) known = st || brood ? 'structure' : 'empty'
        else if (brood) known = 'structure'
        else if (st) {
            const damaged = st.footprint.some(f => hf.subplots[f]?.destroyed)
            const rocket = (st.rocketCells || []).includes(s)
            if (damaged || rocket || c.scanned) known = 'structure'
            if (rocket) rockets.push(s)
        } else if (c.scanned) known = 'empty'
        cells[s] = { known, destroyed: !!c.destroyed, scanned: !!c.scanned }
        if (c.distance != null || c.distanceLetter != null) {
            stamps.push({ s, distance: c.distance, letter: c.distanceLetter ?? null, dead: !!c.distanceDead })
        }
    }
    // Structures the board draws whole: any hit one (damaged or dead).
    for (const id of Object.keys(hf?.broods ?? {})) {
        const b = hf.broods[id]
        if (isBroodDead(hf, id)) continue   // a dead brood is swept away at resolution
        broods.push({ col: b.corridor, centerS: b.footprint[0], footprint: [...b.footprint],
            underConstruction: !!b.underConstruction, egg: b.egg ?? null, spent: !!b.spent })
    }
    for (const id of Object.keys(hf?.structures ?? {})) {
        const st = hf.structures[id]
        if (!st.footprint.some(f => hf.subplots[f]?.destroyed)) continue
        if (st.footprint.some(f => grouped.has(f))) continue
        st.footprint.forEach(f => grouped.add(f))
        groups.push({ cells: [...st.footprint], dead: isStructureDestroyed(hf, id) })
    }
    return { cells, groups, broods, rockets, stamps }
}

// A player's view of the war, from `seat` ('p1' | 'p2').
// war: { round, capacities, p1, p2 } with each player
//      { board, homefront, yield, structuresLost } (resolveRound's players).
// memory: { pastBroodCells } — what the player remembers from earlier rounds
// (rememberRound keeps it up to date).
export function playerView(war, seat, memory = null) {
    const other = seat === 'p1' ? 'p2' : 'p1'
    const me = war[seat], them = war[other]
    const grid = viewOfOpponentGrid(them.homefront)
    grid.pastBroodCells = [...new Set([...(memory?.pastBroodCells ?? []), ...grid.broods.flatMap(b => b.footprint)])].sort((a, b) => a - b)
    return {
        seat,
        round: war.round,
        capacities: clone(war.capacities ?? null),
        self: {
            board: clone(me.board),
            homefront: clone(parse(me.homefront)),
            yield: me.yield ?? 0,
            structuresLost: me.structuresLost ?? 0,
        },
        opp: {
            board: clone(them.board),
            structuresLost: them.structuresLost ?? 0,
            grid,
        },
    }
}

// The memory after a round: the brood cells this view knew.
export function rememberRound(view) {
    return { pastBroodCells: view.opp.grid.pastBroodCells }
}
